import BaseModel from '$shared/models/BaseModel';
import DateString from '$shared/models/DateString';
import type { IEntityParameter } from '$shared/interfaces/api/IEntityParameter';
import { EntityParameterType } from '$shared/constants/EntityParameterType';
import { MapCertificateTypeWithLabel } from '$shared/constants/CertificateType';
import { SchemaType } from '$shared/constants/SchemaType';
import { schemaData } from '$shared/lib/data/SchemaData';

export class EntityParam extends BaseModel<IEntityParameter> {
  public get type(): EntityParameterType | null {
    return this.response?.type || null;
  }

  public get data(): IEntityParameter {
    return this.response;
  }

  get value(): string {
    switch (this.data.type) {
      case EntityParameterType.ProviderName: {
        return [this.data.firstName, this.data.lastName].filter(Boolean).join(' ') || '';
      }
      case EntityParameterType.ProviderNpi: {
        return this.data?.number?.toString() || '';
      }
      case EntityParameterType.ProviderDateOfBirth: {
        return DateString.create(this.data?.dateOfBirth)?.getParsedDate() || '';
      }
      case EntityParameterType.ProviderLicense: {
        const licenseLabel = `${this.data?.number || ''} (${this.data?.state || ''})`;
        if (this.data?.isPrimary) {
          return `${licenseLabel} - Primary`;
        }
        return licenseLabel;
      }
      case EntityParameterType.ProviderSsn: {
        return this.data.ssn || '';
      }
      case EntityParameterType.ProviderAddress: {
        return (
          [
            this.data?.addressLine,
            this.data?.addressLine2,
            this.data?.city,
            this.data.state,
            this.data.zipCode
          ]
            .filter(Boolean)
            .join(', ') || ''
        );
      }
      case EntityParameterType.ProviderEducation: {
        const schoolInfo =
          [this.data.schoolName, this.data.schoolCode].filter(Boolean).join(' - ') || '';
        return [schoolInfo, this.data.degree].filter(Boolean).join(', ') || '';
      }
      case EntityParameterType.ProviderBoardCertificationNumber: {
        return this.data.boardCertificationNumber || '';
      }
      case EntityParameterType.ProviderDea: {
        return this.data.registrationNumber || '';
      }
      case EntityParameterType.ProviderCertificate: {
        if (!this.data?.certificateType) return this.data?.certificateNumber || '';

        const parsedType = MapCertificateTypeWithLabel[this.data?.certificateType];
        if (!this.data?.certificateNumber) return parsedType || '';

        return `${parsedType || this.data?.certificateType} (${this.data?.certificateNumber})`;
      }
      case EntityParameterType.ProviderType: {
        return this.data?.name || '';
      }
      case EntityParameterType.ProviderNccpaBoardCertificateInfo: {
        return this.data?.number?.toString() || '';
      }
      case EntityParameterType.ProviderGender: {
        return this.data?.gender
          ? schemaData.getSchemaLabel(SchemaType.Gender, this.data?.gender)
          : '';
      }
      case EntityParameterType.FacilityName: {
        return this.data.name || '';
      }

      case EntityParameterType.FacilityAccreditationInfo: {
        return (
          [this.data.accreditationNumber, this.data.accreditationProgram]
            .filter(Boolean)
            .join(' - ') || ''
        );
      }
      case EntityParameterType.FacilityCmsCertificationInfo: {
        return (
          [this.data.certificateNumber, this.data.certificateType].filter(Boolean).join(' - ') || ''
        );
      }
      default: {
        return '';
      }
    }
  }

  static find(type: EntityParameterType, params: EntityParam[]): EntityParam | null {
    return params.find((param) => param.type === type) || null;
  }
}
